import { useMemo } from 'react';
import { expandRecurringEvents } from '../../../shared/lib/recurrenceUtils';
import { toUserZoned } from '../../../shared/lib/tzDate';
import { STAGES, ROW_STAGE_ORDER, expandRecurrentTasks } from '../lib/taskGrid';

const isDone = (t) => t.checked || t.stage === 'done';

// Extraído de `ComponentTaskHoy.jsx` (02.1-b6-taskhoy-orchestrator.md, paso B1) -- toda la
// derivación pura de listas/métricas de tareas: sin refs de DOM, sin efectos, sin handlers.
// Único hook de este plan verificable comparando listas/kanban/gantt antes y después, sin
// interacción de por medio.
export function useTaskPipeline({
    tasks, calendarEvents, currentRange, userTz, timeRange, dateOffset, searchQuery,
    taskFilterUsers, systemUsers, user, activeTab, currentPage, itemsPerPage, todayStr, translate,
    // Integración main→dev, epic C2: la vista de LISTA agrupa por etapa con encabezados
    // colapsables y sin paginación. `collapsedStages` = `{ [stage]: true }`.
    collapsedStages = {}, showCompletedTasks = true, showSubtasksAsNested = true,
}) {
    const expandedTasks = useMemo(
        () => expandRecurrentTasks(tasks, currentRange.startStr, currentRange.endStr, userTz),
        [tasks, currentRange, userTz]
    );

    const filteredTasks = useMemo(() => expandedTasks.filter(t => {
        const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()));

        if (!matchesSearch) return false;

        // Si no hay usuarios seleccionados en el filtro, mostrar TODAS las tareas (comportamiento de 'Todos').
        if (!taskFilterUsers || taskFilterUsers.length === 0) {
            // No user filter active, don't filter out by assignee.
        } else {
            const isMatch = taskFilterUsers.some(u => {
                const uIdStr = String(u.id);
                const uNickLower = u.nick ? u.nick.toLowerCase() : '';

                // Compare with assignee
                const assigneeMatch = (t.assignee && t.assignee.toLowerCase().includes(uNickLower)) || String(t.assignee_id) === uIdStr;
                // Compare with supervisor
                const supervisorMatch = (t.supervisor && t.supervisor.toLowerCase().includes(uNickLower)) || String(t.supervisor_id) === uIdStr;

                return assigneeMatch || supervisorMatch;
            });
            if (!isMatch) return false;
        }

        // Date filtering
        if (!t.date && !t.start_time) {
            return timeRange === 'hoy' && dateOffset === 0;
        }

        if (timeRange !== 'custom') {
            let taskDateStr = t.date;
            if (t.start_time) {
                const d = new Date(t.start_time);
                // Utiliza la función que ya existe en el archivo (asumiendo toUserZoned está disponible globalmente en el componente)
                // Si no, podemos hacerlo manual con toLocaleDateString
                const parts = new Intl.DateTimeFormat('en-US', { timeZone: userTz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
                const year = parts.find(p => p.type === 'year').value;
                const month = parts.find(p => p.type === 'month').value;
                const day = parts.find(p => p.type === 'day').value;
                taskDateStr = `${year}-${month}-${day}`;
            }

            if (taskDateStr < currentRange.startStr || taskDateStr > currentRange.endStr) {
                return false;
            }
        }

        return true;
    }), [expandedTasks, searchQuery, taskFilterUsers, timeRange, dateOffset, currentRange]);

    const standardTasks = useMemo(() => {
        // `parent_task_id` reemplaza
        // el viejo `subtasks[]` embebido en el padre -- cada tarea ya sabe si es una subtarea
        // (trae su propio `parent_task_id`), no hace falta juntar los arrays de todos los padres.
        const allSubtaskIds = new Set(tasks.filter(t => t.parent_task_id).map(t => t.id));

        return [...filteredTasks].filter((t, index, self) => {
            if (showSubtasksAsNested && allSubtaskIds.has(t.id)) return false; // Hide subtasks from top-level
            const idToMatch = t.original_id || t.id;
            return index === self.findIndex(x => (x.original_id || x.id) === idToMatch);
        }).map(t => ({
            ...t,
            id: t.original_id || t.id // Restore real ID so table interactions work natively
        })).sort((a, b) => {
            const orderA = ROW_STAGE_ORDER[a.stage] || 99;
            const orderB = ROW_STAGE_ORDER[b.stage] || 99;
            if (orderA !== orderB) return orderA - orderB;

            if (a.stage === 'planeada') return 0;
            const timeA = new Date(a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
        });
    }, [filteredTasks, tasks]);

    const taskMetrics = useMemo(() => {
        const total = standardTasks.length;
        const done = standardTasks.filter(t => t.stage === 'done').length;
        const percentage = total > 0 ? Math.round((done / total) * 100) : 0;
        return { total, done, percentage };
    }, [standardTasks]);

    const unassignedTasks = useMemo(() => expandedTasks.filter(t => {
        const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()));
        if (!matchesSearch) return false;

        const hasAssignee = t.assignee && t.assignee.trim() !== '';
        const hasSupervisor = t.supervisor && t.supervisor.trim() !== '';
        if (hasAssignee || hasSupervisor) return false;

        if (!t.date) return timeRange === 'hoy' && dateOffset === 0;
        if (timeRange !== 'custom' && (t.date < currentRange.startStr || t.date > currentRange.endStr)) return false;

        return true;
    }), [expandedTasks, searchQuery, timeRange, dateOffset, currentRange]);

    // Integración main→dev, epic C2: la vista de LISTA se agrupa por etapa. Cada grupo lleva
    // una fila divisoria `isStageDivider` (con `count`) y sus tareas van debajo salvo que la
    // etapa esté colapsada (`collapsedStages[stage]`). Se emiten TODAS las etapas de `STAGES`
    // aunque estén vacías, y al final las etapas fuera del catálogo que tengan tareas.
    const listStandardTasks = useMemo(() => {
        let list = [...standardTasks];
        if (!showCompletedTasks) list = list.filter(t => !isDone(t));

        if (taskFilterUsers && taskFilterUsers.length > 0) {
            const allSubtaskIds = new Set(tasks.filter(t => t.parent_task_id).map(t => t.id));
            const unassigned = unassignedTasks.filter((t, index, self) => {
                if (showSubtasksAsNested && allSubtaskIds.has(t.id)) return false;
                const idToMatch = t.original_id || t.id;
                return index === self.findIndex(x => (x.original_id || x.id) === idToMatch);
            }).map(t => ({ ...t, id: t.original_id || t.id, _isUnassigned: true }))
                .filter(t => showCompletedTasks || !isDone(t));
            list = list.concat(unassigned);
        }

        const grouped = {};
        list.forEach(t => {
            const s = t.stage || 'planeada';
            (grouped[s] = grouped[s] || []).push(t);
        });

        const finalGroupedList = [];
        const emitStage = (s) => {
            const groupTasks = grouped[s] || [];
            finalGroupedList.push({ id: `stage_divider_${s}`, isDivider: true, isStageDivider: true, stage: s, count: groupTasks.length });
            if (!collapsedStages[s]) finalGroupedList.push(...groupTasks);
        };
        STAGES.forEach(emitStage);
        Object.keys(grouped).forEach(s => {
            if (!STAGES.includes(s) && grouped[s].length > 0) emitStage(s);
        });
        return finalGroupedList;
    }, [standardTasks, taskFilterUsers, unassignedTasks, tasks, collapsedStages, showCompletedTasks]);

    const ganttTasks = useMemo(() => {
        const expandedCalendarEvents = expandRecurringEvents(
            calendarEvents,
            new Date(`${currentRange.startStr}T00:00:00`),
            new Date(`${currentRange.endStr}T23:59:59`)
        );

        return [
            ...filteredTasks.filter(t => {
                if (t.stage === 'done') return false; // Hide done tasks from gantt
                if (t.duration_cols !== undefined && t.start_col !== undefined) return true;
                return false;
            }),
            ...expandedCalendarEvents.filter(re => re.type !== 'task').map(re => {
                const startDate = re.start ? new Date(re.start) : new Date(re.start_time);
                const endDate = re.end ? new Date(re.end) : new Date(re.end_time);
                // Fase timezones: posición en la grilla y el día calendario del evento se leen
                // en `userTz`, no en el navegador.
                const zonedStart = toUserZoned(startDate, userTz);
                const startHour = zonedStart.getHours();
                const startMinutes = zonedStart.getMinutes();
                // Start column (08:00 is col 0, 30 min intervals)
                const startCol = (startHour - 8) * 2 + (startMinutes >= 30 ? 1 : 0);
                const durationMins = (endDate - startDate) / (1000 * 60);
                const durationCols = Math.max(1, durationMins / 30);

                const localDate = zonedStart;

                const isCreator = re.created_by && user && String(re.created_by) === String(user.dbId);
                const isInvited = !isCreator; // Si no es el creador, es invitado (amarillo)

                // Extract the original ID if it's a virtual recurring instance like "uuid_2026-05-01"
                const originalEventId = re.id && String(re.id).includes('_') ? String(re.id).split('_')[0] : re.id;

                const ownerId = re.created_by || re.createdBy;
                const ownerUser = systemUsers.find(u => String(u.dbId) === String(ownerId) || String(u.id) === String(ownerId));
                const ownerNick = ownerUser ? (ownerUser.nick || ownerUser.name) : (re.ownerNick || 'Sin Asignar');

                const eventUsers = new Set();
                if (ownerNick && ownerNick !== 'Sin Asignar') {
                    eventUsers.add(ownerNick);
                }
                if (re.attendees && Array.isArray(re.attendees)) {
                    re.attendees.forEach(a => {
                        const aNick = a.name || a.nick;
                        if (aNick) eventUsers.add(aNick);
                    });
                }
                const assigneeString = eventUsers.size > 0 ? Array.from(eventUsers).join(',') : 'Sin Asignar';

                return {
                    id: `event-${re.id}`, // Maintain uniqueness for recurrence instances
                    originalEventId: originalEventId, // Keep for navigation
                    title: re.title || translate('common.untitled'),
                    stage: isInvited ? 'event-invited' : 'event-black', // mapped to CSS
                    start_col: startCol,
                    duration_cols: durationCols,
                    date: `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`, // YYYY-MM-DD en userTz
                    start_time: startDate.toISOString(),
                    end_time: endDate.toISOString(),
                    is_calendar_event: true,
                    is_invited: isInvited,
                    is_recurring: !!re.recurrence,
                    assignee: assigneeString // Needed for grouping.
                };
            }).filter(re => re.start_col >= 0 && re.start_col <= 24) // only keep if within 08:00-20:00 range
        ].filter(t => {
            if (!t.date) return false;
            return t.date >= currentRange.startStr && t.date <= currentRange.endStr;
        });
    }, [filteredTasks, calendarEvents, currentRange, systemUsers, user]);

    // Calculate Page Items. Integración main→dev, epic C2: la pestaña de LISTA ya no pagina
    // (`287be8f7`) — se renderizan todos los grupos de etapa de una; el resto de pestañas
    // conserva la paginación.
    const isListTab = activeTab === 'lista';
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const basePaginationTasks = isListTab ? listStandardTasks : standardTasks;
    const currentParentTasks = isListTab
        ? basePaginationTasks
        : basePaginationTasks.slice(indexOfFirstItem, indexOfLastItem);

    const currentTasks = [];
    currentParentTasks.forEach(parent => {
        if (parent.isDivider) {
            currentTasks.push(parent);
            return;
        }
        currentTasks.push({ ...parent, isSubtask: !showSubtasksAsNested && parent.parent_task_id ? true : false });
        
        if (showSubtasksAsNested) {
            // `parent_task_id` reemplaza
            // el viejo `parent.subtasks[]` -- se buscan directo las tareas cuyo `parent_task_id`
            // apunte a esta, en vez de resolver ids desde un array embebido en el padre.
            tasks.filter(t => t.parent_task_id === parent.id && (showCompletedTasks || !isDone(t))).forEach(childTask => {
                currentTasks.push({ ...childTask, isSubtask: true, depth: 1, parentId: parent.id });
            });
        }
    });

    const totalPages = isListTab ? 1 : Math.max(1, Math.ceil(basePaginationTasks.length / itemsPerPage));

    // Metrics Calculation
    // NOTA (paso B1): `metrics` (a diferencia de `taskMetrics`, que sí se usa para "% tareas
    // hechas") no tiene ningún consumidor en el render -- confirmado con grep antes de mover.
    // Se preserva verbatim, sin arreglar (mismo criterio que el nodo "trigger" muerto de
    // `ComponentTaskFlow.jsx`), documentado acá en vez de en el container.
    const getMetrics = () => {
        const completed = tasks.filter(t => t.checked || t.stage === 'done').length;
        const pending = tasks.filter(t => !t.checked && t.stage !== 'done').length;

        // Hallazgo fuera del alcance original (sevilla-timezones): estaba hardcodeado contra
        // '2026-06-01' fijo ("mock", según el comentario original) en vez de la fecha real de
        // hoy -- contaba casi todas las tareas no terminadas como atrasadas. "Hoy" ahora se
        // calcula en `userTz` (todayStr), no en el navegador.
        const atrasados = tasks.filter(t => {
            if (t.checked || t.stage === 'done') return false;
            if (!t.date) return false;
            return t.date < todayStr;
        }).length;

        // Comentarios: Sum comments count
        let totalComments = 0;
        tasks.forEach(t => {
            if (Array.isArray(t.comments)) {
                totalComments += t.comments.length;
            }
        });
        if (totalComments === 0) totalComments = 3; // elegant fallback if empty

        const todo = tasks.filter(t => t.stage === 'planeada').length;
        const bloqueados = tasks.filter(t => t.stage === 'bloqueo').length;

        return { completed, pending, atrasados, comments: totalComments, todo, bloqueados };
    };

    const metrics = getMetrics();

    // SVG Flow Chart Coordinates Calculation
    // NOTA (paso B1): `flowNodes`/`flowLinks` no tienen ningún consumidor en el render --
    // confirmado con grep antes de mover (ya lo marcaba `no-unused-vars` en el container).
    // Se preserva verbatim, sin arreglar, mismo criterio que `metrics` arriba.
    const getFlowNodesAndLinks = () => {
        const columns = {
            'bloqueo': [],
            'retraso': [],
            'synk': [],
            'en proceso': [],
            'planeada': [],
            'check': [],
            'done': []
        };

        // Group active tasks
        standardTasks.forEach(task => {
            const col = columns[task.stage] ? task.stage : 'planeada';
            columns[col].push(task);
        });

        const nodes = [];
        const colWidth = 140;
        const startX = 50;

        // Compute (x, y) coordinates for nodes
        STAGES.forEach((stage, sIdx) => {
            const list = columns[stage] || [];
            const colX = startX + sIdx * colWidth;
            const totalY = list.length * 80;
            const startY = Math.max(20, (300 - totalY) / 2); // Center items vertically

            list.forEach((task, tIdx) => {
                nodes.push({
                    id: task.id,
                    title: task.title,
                    stage: task.stage,
                    assignee: task.assignee,
                    x: colX,
                    y: startY + tIdx * 80,
                    priority: task.priority
                });
            });
        });

        // Compute sequential connecting links
        const links = [];
        for (let i = 0; i < nodes.length - 1; i++) {
            // Sequential mapping for flow visual links (Task A -> Task B -> Task C)
            links.push({
                source: nodes[i],
                target: nodes[i + 1]
            });
        }

        return { nodes, links };
    };

    const { nodes: flowNodes, links: flowLinks } = getFlowNodesAndLinks();

    return {
        expandedTasks, filteredTasks, standardTasks, taskMetrics, unassignedTasks,
        listStandardTasks, ganttTasks, currentTasks, totalPages, metrics, flowNodes, flowLinks,
    };
}
